import { buildReceiptPdfSummary } from './receiptPdfSummary';
import { buildReceiptItemDescription, buildReceiptItemQuantity } from './receiptItemDisplay';
import { getContactDocumentName } from '../../utils/contactDisplayName';

const PAGE = { width: 595.28, height: 841.89 };
const LEFT = 27;
const RIGHT = 546;
const BLUE = '#0080ff';
const GRAY = '#e0e0e0';
const money = value => (Number(value) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const number = value => Number(value) || 0;

const thaiDate = (value, dayjs) => {
  if (!value) return '-';
  const date = dayjs(value);
  return date.isValid() ? `${date.format('DD/MM')}/${date.year() + 543}` : '-';
};

const positionedText = (text, x, y, width, options = {}) => ({
  columns: [{ width, text: String(text ?? ''), ...options }],
  columnGap: 0,
  absolutePosition: { x, y }
});

const line = (x1, y1, x2, y2, width = 0.65) => ({
  type: 'line', x1, y1, x2, y2, lineWidth: width, lineColor: '#181818'
});

const rect = (x, y, w, h, options = {}) => ({
  type: 'rect', x, y, w, h, lineWidth: options.lineWidth ?? 0.65,
  lineColor: options.lineColor || '#181818',
  ...(options.color ? { color: options.color } : {}),
  ...(options.r ? { r: options.r } : {})
});

const estimateHeight = (description, fontScale) => {
  const charactersPerLine = Math.floor(35 / fontScale);
  const lineCount = String(description || '').split('\n')
    .reduce((sum, part) => sum + Math.max(1, Math.ceil(Array.from(part).length / charactersPerLine)), 0);
  return Math.max(25 * fontScale, lineCount * 14 * fontScale + 8);
};

const splitLongItem = (item, fontScale) => {
  if (estimateHeight(item.description, fontScale) <= 180) return [item];
  const visualLines = String(item.description).split('\n').flatMap(part => {
    const characters = Array.from(part);
    if (!characters.length) return [''];
    const lines = [];
    const chunkSize = Math.floor(30 / fontScale);
    for (let index = 0; index < characters.length; index += chunkSize) {
      lines.push(characters.slice(index, index + chunkSize).join(''));
    }
    return lines;
  });
  const segments = [];
  for (let index = 0; index < visualLines.length; index += 9) {
    const first = index === 0;
    segments.push({
      ...item,
      description: visualLines.slice(index, index + 9).join('\n'),
      index: first ? item.index : '',
      quantity: first ? item.quantity : '',
      unitPrice: first ? item.unitPrice : '',
      discount: first ? item.discount : '',
      amount: first ? item.amount : ''
    });
  }
  return segments;
};

const paginateItems = (items, fontScale) => {
  const pages = [[]];
  let used = 0;
  for (const item of items.flatMap(item => splitLongItem(item, fontScale))) {
    const height = estimateHeight(item.description, fontScale);
    if (pages[pages.length - 1].length && used + height > 190) {
      pages.push([]);
      used = 0;
    }
    pages[pages.length - 1].push({ ...item, height });
    used += height;
  }
  return pages;
};

export const createDeliveryInvoiceDefinition = ({
  company = {}, receipt = {}, logoBase64, fixedSignatureLogo, fixedStampLogo,
  printOptions = {}, dayjs, numberToThaiText
}) => {
  const summary = buildReceiptPdfSummary(receipt);
  const deposit = Math.max(0, number(receipt.deposit_amount ?? receipt.advance_amount));
  const netAmount = Math.max(0, summary.grandTotal - deposit);
  const invoiceNumber = receipt.invoice_number || receipt.receipt_number || '-';
  const customerName = getContactDocumentName(receipt);
  const salesperson = printOptions.salesperson_name || receipt.salesperson_name || receipt.sale_name || receipt.created_by_name || '-';
  const contact = receipt.contact_person || (receipt.contact_name && receipt.contact_name !== customerName
    ? receipt.contact_name : receipt.phone) || '-';
  const requestedFontScale = Number(printOptions.delivery_invoice_font_scale);
  const fontScale = [0.9, 1, 1.1].includes(requestedFontScale) ? requestedFontScale : 1;
  const showDocumentDate = (printOptions.show_document_date ?? printOptions.delivery_invoice_show_date) !== false;
  const referenceY = showDocumentDate ? 191 : 159;
  const salespersonY = showDocumentDate ? 225 : 193;
  const textAt = (text, x, y, width, options = {}) => positionedText(text, x, y, width, {
    ...options,
    fontSize: (options.fontSize ?? 10.5) * fontScale
  });
  const items = (Array.isArray(receipt.items) ? receipt.items : []).map((item, index) => {
    const isFree = item?.is_free === true || Number(item?.is_free) === 1;
    return {
      index: index + 1,
      description: buildReceiptItemDescription(item),
      quantity: buildReceiptItemQuantity(item),
      unitPrice: isFree ? 'แถมฟรี' : money(item.unit_price),
      discount: money(item.discount_amount ?? item.discount),
      amount: money(isFree ? 0 : (item.total_price ?? number(item.quantity) * number(item.unit_price)))
    };
  });
  const pages = paginateItems(items, fontScale);
  const content = [];

  pages.forEach((pageItems, pageIndex) => {
    const finalPage = pageIndex === pages.length - 1;
    const shapes = [
      rect(437, 31, 107, 36, { r: 7 }),
      rect(319, 83, 227, 36, { r: 4, color: BLUE }),
      rect(LEFT, 119, RIGHT - LEFT, 143, { r: 5 }),
      line(319, 119, 319, 262),
      rect(LEFT, 265, RIGHT - LEFT, 239),
      rect(LEFT, 265, RIGHT - LEFT, 36, { color: GRAY }),
      ...[80, 270, 319, 393, 469].map(x => line(x, 265, x, 504)),
      rect(LEFT, 504, RIGHT - LEFT, 180),
      line(319, 504, 319, 684),
      line(469, 504, 469, 684),
      ...[540, 576, 612, 648].map(y => line(319, y, RIGHT, y)),
      rect(LEFT, 684, RIGHT - LEFT, 35, { r: 3, color: GRAY }),
      rect(LEFT, 723, RIGHT - LEFT, 72, { r: 3 }),
      line(63, 752, 226, 752, 0.5),
      line(327, 752, 490, 752, 0.5),
      line(LEFT, 798, RIGHT, 798)
    ];
    content.push({ canvas: shapes, absolutePosition: { x: 0, y: 0 } });

    if (logoBase64) content.push({ image: logoBase64, fit: [65, 65], absolutePosition: { x: 38, y: 33 } });
    content.push(
      textAt(company.company_name || company.name || 'ชื่อกิจการ', 103, 29, 290, { fontSize: 17, bold: true }),
      textAt(company.address || '-', 103, 52, 300, { fontSize: 12 }),
      textAt(`เลขประจำตัวผู้เสียภาษี ${company.tax_id || '-'}`, 103, 86, 300, { fontSize: 12 }),
      textAt('ต้นฉบับ\nORIGINAL', 437, 36, 107, { alignment: 'center', fontSize: 12.5, lineHeight: 0.8 }),
      textAt('ใบส่งของ/ใบแจ้งหนี้', 319, 89, 227, { alignment: 'center', fontSize: 14.5, bold: true }),
      textAt('ลูกค้า :\nCustomer', 35, 125, 55, { fontSize: 11, lineHeight: 0.82 }),
      textAt(customerName, 80, 127, 230, { fontSize: 12.5 }),
      textAt('เลขประจำตัวผู้เสียภาษี :\nTax No.', 35, 157, 112, { fontSize: 11, lineHeight: 0.82 }),
      textAt(receipt.tax_id || '-', 150, 158, 155, { fontSize: 12 }),
      textAt('ผู้ติดต่อ :\nAttn.', 35, 186, 55, { fontSize: 11, lineHeight: 0.82 }),
      textAt(contact, 80, 187, 230, { fontSize: 12 }),
      textAt('ที่อยู่ :\nAddress', 35, 216, 55, { fontSize: 11, lineHeight: 0.82 }),
      textAt(receipt.address || '-', 80, 217, 229, { fontSize: 12 }),
      textAt('เลขที่ :\nNo.', 332, 127, 50, { fontSize: 11, lineHeight: 0.82 }),
      textAt(invoiceNumber, 383, 127, 155, { fontSize: 12 }),
      textAt('อ้างอิง :\nReference', 332, referenceY, 60, { fontSize: 11, lineHeight: 0.82 }),
      textAt(receipt.reference_no || receipt.quote_number || '-', 393, referenceY, 145, { fontSize: 12 }),
      textAt('พนักงานขาย :\nSalesman', 332, salespersonY, 67, { fontSize: 11, lineHeight: 0.82 }),
      textAt(salesperson, 399, salespersonY, 139, { fontSize: 12 })
    );
    if (showDocumentDate) content.push(
      textAt('วันที่ :\nDate', 332, 159, 50, { fontSize: 11, lineHeight: 0.82 }),
      textAt(thaiDate(receipt.receipt_date, dayjs), 383, 159, 155, { fontSize: 12 })
    );

    const headings = [
      ['ลำดับ\nItem', 27, 53], ['รายการสินค้า\nDescription', 80, 190],
      ['จำนวน\nQuantity', 270, 49], ['ราคา/หน่วย\nUnit price', 319, 74],
      ['ส่วนลด\nDiscount', 393, 76], ['จำนวนเงิน\nAmount', 469, 77]
    ];
    headings.forEach(([label, x, width]) => content.push(
      textAt(label, x, 270, width, { alignment: 'center', fontSize: 11.5, lineHeight: 0.86 })
    ));

    let itemY = 306;
    pageItems.forEach(item => {
      content.push(
        textAt(item.index, 29, itemY, 48, { alignment: 'center', fontSize: 12 }),
        textAt(item.description, 85, itemY, 181, { fontSize: 12, lineHeight: 0.9 }),
        textAt(item.quantity, 272, itemY, 45, { alignment: 'center', fontSize: 12 }),
        textAt(item.unitPrice, 321, itemY, 68, { alignment: 'right', fontSize: 12 }),
        textAt(item.discount, 395, itemY, 70, { alignment: 'right', fontSize: 12 }),
        textAt(item.amount, 471, itemY, 70, { alignment: 'right', fontSize: 12 })
      );
      itemY += item.height;
    });

    content.push(
      textAt('หมายเหตุ :\nRemark', 35, 513, 95, { fontSize: 11.5, lineHeight: 0.85 }),
      textAt(finalPage ? (receipt.notes || '') : 'ต่อหน้าถัดไป', 36, 545, 273, { fontSize: 12 }),
      textAt(finalPage ? numberToThaiText(netAmount) : '', 30, 691, 280, { alignment: 'center', fontSize: 12 }),
      textAt('รวมสุทธิ\nNet Amount', 374, 689, 85, { alignment: 'right', fontSize: 11.5, lineHeight: 0.83 }),
      textAt(finalPage ? money(netAmount) : '', 472, 692, 69, { alignment: 'right', fontSize: 12 }),
      textAt('ลงชื่อ', 40, 739, 50, { fontSize: 12 }),
      textAt('ผู้รับของ', 233, 739, 65, { fontSize: 12 }),
      textAt('ลงชื่อ', 304, 739, 50, { fontSize: 12 }),
      textAt('ผู้ส่งของ', 496, 739, 50, { fontSize: 12 }),
      textAt('หมายเหตุ : ชำระเงินแล้วทางร้านจะออกใบเสร็จรับเงินให้ตามกฎหมาย', 36, 772, 480, { fontSize: 11.5 })
    );
    const totalRows = [
      ['รวม\nTotal', summary.grossAmount],
      ['ส่วนลด\nDiscount', summary.discountAmount],
      ['ราคาก่อนภาษี\nValue Amount', summary.netAmount],
      ['มัดจำ\nDeposit', deposit],
      ['ภาษีมูลค่าเพิ่ม\nVAT', summary.taxAmount]
    ];
    totalRows.forEach(([label, amount], index) => content.push(
      textAt(label, 324, 511 + index * 36, 138, { alignment: 'right', fontSize: 11.5, lineHeight: 0.82 }),
      textAt(finalPage ? money(amount) : '', 472, 515 + index * 36, 69, { alignment: 'right', fontSize: 12 })
    ));

    if (finalPage && fixedSignatureLogo) {
      content.push({ image: fixedSignatureLogo, fit: [140, 36], absolutePosition: { x: 340, y: 721 } });
    }
    if (finalPage && fixedStampLogo) {
      content.push({ image: fixedStampLogo, fit: [35, 35], absolutePosition: { x: 503, y: 754 } });
    }
    if (!finalPage) content.push({ text: ' ', fontSize: 1, pageBreak: 'after' });
  });

  return {
    pageSize: PAGE,
    pageMargins: [0, 0, 0, 0],
    defaultStyle: { font: 'THSarabunNew', fontSize: 10.5 * fontScale, color: '#111111', lineHeight: 0.95 },
    content
  };
};
